import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Copy, ChevronUp, ChevronDown, Bold, AlignLeft, AlignCenter, AlignRight, Eye, EyeOff } from 'lucide-react';

const TEXT_COLORS = ['#111111', '#FFFFFF', '#3A7BFF', '#FF6B9D', '#22C55E', '#F59E0B'];
const SHAPE_COLORS = ['#3A7BFF', '#FF6B9D', '#22C55E', '#F59E0B', '#A855F7', '#111111'];

/**
 * Contextual Properties for the selected canvas element — opens
 * automatically in the left tool panel (see ImagesAndPosts.jsx), same
 * pattern as the Main Editor Inspector and Clips Properties view.
 */
export function DesignElementProperties({ element, onChange, onDelete, onDuplicate, onBringForward, onSendBackward, onToggleVisibility }) {
  if (!element) {
    return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="design-properties-empty">Select an element on the canvas to edit it.</p>;
  }

  return (
    <div className="space-y-3" data-testid="design-element-properties">
      <p className="text-[11px] text-[#8A93A6] uppercase tracking-wide">{element.type} element</p>

      {element.type === 'text' && (
        <>
          <Textarea value={element.text || ''} onChange={(e) => onChange({ text: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm" rows={2} data-testid="design-text-content-input" />
          <div>
            <label className="text-[11px] text-[#8A93A6] mb-1 block">Size ({element.fontSize || 24}px)</label>
            <input type="range" min={10} max={96} value={element.fontSize || 24} onChange={(e) => onChange({ fontSize: Number(e.target.value) })} className="w-full accent-[#3A7BFF]" data-testid="design-text-size-slider" />
          </div>
          <div className="flex items-center gap-1.5">
            {TEXT_COLORS.map((c) => (
              <button key={c} onClick={() => onChange({ color: c })} className={`h-6 w-6 rounded-full border-2 ${element.color === c ? 'border-[#3A7BFF]' : 'border-white/20'}`} style={{ background: c }} data-testid={`design-text-color-${c.replace('#', '')}`} />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant={element.bold ? 'default' : 'outline'} className={element.bold ? 'bg-[#3A7BFF]' : 'border-white/10'} onClick={() => onChange({ bold: !element.bold })} data-testid="design-text-bold-toggle"><Bold className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant={element.align === 'left' || !element.align ? 'default' : 'outline'} className={element.align === 'left' || !element.align ? 'bg-[#3A7BFF]' : 'border-white/10'} onClick={() => onChange({ align: 'left' })} data-testid="design-text-align-left"><AlignLeft className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant={element.align === 'center' ? 'default' : 'outline'} className={element.align === 'center' ? 'bg-[#3A7BFF]' : 'border-white/10'} onClick={() => onChange({ align: 'center' })} data-testid="design-text-align-center"><AlignCenter className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant={element.align === 'right' ? 'default' : 'outline'} className={element.align === 'right' ? 'bg-[#3A7BFF]' : 'border-white/10'} onClick={() => onChange({ align: 'right' })} data-testid="design-text-align-right"><AlignRight className="h-3.5 w-3.5" /></Button>
          </div>
        </>
      )}

      {element.type === 'image' && (
        <>
          <Input value={element.src || ''} onChange={(e) => onChange({ src: e.target.value })} placeholder="Image URL (or add from Media)" className="h-8 text-xs bg-white/5 border-white/10" data-testid="design-image-src-input" />
          <Select value={element.objectFit || 'cover'} onValueChange={(v) => onChange({ objectFit: v })}>
            <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="design-image-fit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cover">Fill (crop to frame)</SelectItem>
              <SelectItem value="contain">Fit (show entire image)</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      {element.type === 'shape' && (
        <>
          <Select value={element.shapeType || 'rectangle'} onValueChange={(v) => onChange({ shapeType: v })}>
            <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="design-shape-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rectangle">Rectangle</SelectItem>
              <SelectItem value="circle">Circle</SelectItem>
              <SelectItem value="line">Line</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            {SHAPE_COLORS.map((c) => (
              <button key={c} onClick={() => onChange({ fill: c })} className={`h-6 w-6 rounded-full border-2 ${element.fill === c ? 'border-white' : 'border-white/20'}`} style={{ background: c }} data-testid={`design-shape-color-${c.replace('#', '')}`} />
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-white/10">
        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={onBringForward} data-testid="design-element-forward"><ChevronUp className="h-3.5 w-3.5 mr-1" />Forward</Button>
        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={onSendBackward} data-testid="design-element-backward"><ChevronDown className="h-3.5 w-3.5 mr-1" />Backward</Button>
        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={onToggleVisibility} data-testid="design-element-visibility">{element.visible === false ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}{element.visible === false ? 'Hidden' : 'Visible'}</Button>
        <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={onDuplicate} data-testid="design-element-duplicate"><Copy className="h-3.5 w-3.5 mr-1" />Duplicate</Button>
      </div>
      <Button size="sm" variant="outline" className="w-full border-red-500/30 text-red-400 hover:text-red-300 text-xs" onClick={onDelete} data-testid="design-element-delete">
        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete Element
      </Button>
    </div>
  );
}
